import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import {
  glassCardStyles,
  gradientTextStyles,
  Page,
  PrimaryButton,
  theme,
  typography,
} from "../Homepage/styles";

/** Override global a:hover underline (shards-dashboards) on card-style links. */
const cardLinkNoUnderline = css`
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }

  &:hover *,
  &:focus *,
  &:active *,
  &:visited * {
    text-decoration: none;
  }
`;

export const MembersPage = styled(Page)`
  background: hsl(var(--hp-background));
  background-image: none;
`;


export const MembersHero = styled.section`
  position: relative;
  padding: 104px 16px 40px;
  overflow: hidden;
  text-align: center;

  @media (min-width: 768px) {
    padding: 128px 24px 48px;
  }
`;

export const MembersHeroBgGradient = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    hsl(var(--hp-primary) / 0.1) 0%,
    hsl(var(--hp-background)) 50%,
    hsl(var(--hp-accent) / 0.05) 100%
  );
`;

export const MembersHeroBgRadial = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at center,
    hsl(var(--hp-primary) / 0.15) 0%,
    transparent 70%
  );
`;

export const MembersHeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 768px;
  margin: 0 auto;
`;

export const MembersHeroTitle = styled.h1`
  margin: 0;
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 2.25rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.25;

  @media (min-width: 768px) {
    font-size: 3.75rem;
  }

  span {
    ${gradientTextStyles}
  }
`;

export const MembersFiltersSection = styled.section`
  padding: 0 16px 32px;
`;

export const MembersContentInner = styled.div`
  max-width: 1152px;
  margin: 0 auto;
`;

export const MembersFilterCard = styled.div`
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: hsl(var(--hp-card) / 0.8);
  backdrop-filter: blur(24px);
  border: 1px solid hsl(var(--hp-border) / 0.5);

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const MembersAnalystFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

export const MembersAnalystButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: ${({ $isAll }) => ($isAll ? "0" : "2px")};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? theme.primary : "hsl(var(--hp-border) / 0.6)"};
  border-radius: 999px;
  background: ${({ $isAll, $selected }) =>
    $isAll
      ? "hsl(var(--hp-secondary) / 0.6)"
      : "transparent"};
  box-shadow: ${({ $selected }) =>
    $selected ? `0 0 0 2px ${theme.bg}, 0 0 0 4px ${theme.primary}` : "none"};
  opacity: ${({ $selected }) => ($selected ? 1 : 0.8)};
  cursor: pointer;
  transition: border-color 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    opacity: 1;
    border-color: ${theme.primary};
  }

  img {
    width: 100%;
    height: 100%;
    border-radius: 999px;
    object-fit: cover;
  }
`;

export const MembersAllLabel = styled.span`
  color: ${theme.foreground};
  font-size: 0.625rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: 0.02em;
`;

export const MembersFilterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;


export const MembersDateFilterRoot = styled.div`
  position: relative;

  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    display: inline-flex;
  }

  .react-datepicker__aria-live {
    display: none;
  }

  .react-datepicker__close-icon {
    top: 50%;
    right: 8px;
    height: 18px;
    width: 18px;
    padding: 0;
    transform: translateY(-50%);

    &::after {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: hsl(var(--hp-muted-foreground) / 0.25) !important;
      color: ${theme.foreground} !important;
      font-size: 11px;
      line-height: 1;
    }

    &:hover::after {
      background: hsl(var(--hp-muted-foreground) / 0.4) !important;
    }
  }
`;

export const MembersDateFilterInput = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 34px 0 14px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 8px;
  background: hsl(var(--hp-background) / 0.4);
  color: ${({ $hasValue }) => ($hasValue ? theme.foreground : theme.textMuted)};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: ${typography.weights.normal};
  line-height: 1.25;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    color 0.2s ease;

  .material-icons {
    flex-shrink: 0;
    color: ${theme.textMuted};
    font-size: 16px;
  }

  &:hover {
    background: hsl(var(--hp-background) / 0.6);
    border-color: hsl(var(--hp-border) / 0.8);
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: 2px;
  }
`;

export const MembersClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${theme.textMuted};
  font-family: inherit;
  font-size: 0.875rem;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;

  .material-icons {
    font-size: 16px;
  }

  &:hover {
    color: ${theme.foreground};
    background: ${theme.secondary};
  }
`;

export const MembersFeedSection = styled.section`
  padding: 0 16px 80px;
`;

export const MembersGuestBanner = styled.div`
  ${glassCardStyles}
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  padding: 20px 24px;
  border-radius: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const MembersGuestBannerText = styled.div`
  p:first-child {
    margin: 0 0 4px;
    color: ${theme.foreground};
    font-size: 0.875rem;
    font-weight: ${typography.weights.semibold};
  }

  p:last-child {
    margin: 0;
    color: ${theme.textMuted};
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

export const MembersGuestBannerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

/** Lovable Button size="sm" outline + hero — same height/radius */
const guestBannerBtnSize = `
  box-sizing: border-box;
  height: 36px;
  min-height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
`;

export const MembersOutlineButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${guestBannerBtnSize}
  border: 1px solid hsl(var(--hp-border) / 0.6);
  background: hsl(var(--hp-background) / 0.4);
  color: ${theme.foreground};
  font-family: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: hsl(var(--hp-muted) / 0.4);
    text-decoration: none;
    color: ${theme.foreground};
  }
`;

export const MembersHeroCtaButton = styled(PrimaryButton)`
  ${guestBannerBtnSize}
  border: none;
  box-shadow: ${theme.shadowButton};

  &:hover {
    transform: none;
    box-shadow: 0 6px 30px hsl(var(--hp-primary) / 0.5);
  }
`;

export const MembersEmptyState = styled.div`
  ${glassCardStyles}
  padding: 48px 24px;
  border-radius: 16px;
  text-align: center;

  p {
    margin: 0 0 16px;
    color: ${theme.textMuted};
    font-size: 1rem;
    line-height: 1.6;
  }
`;

export const MembersGrid = styled.div`
  display: grid;
  gap: 24px;
  margin-top: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    /* Page size is 7. Page 1 uses 6 grid cards; later pages leave one card. */
    & > *:last-child:nth-child(odd) {
      grid-column: 1 / -1;
      width: 100%;
      max-width: calc((100% - 24px) / 2);
      justify-self: center;
    }
  }
`;

export const MembersPaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const MembersPaginationNav = styled.nav`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: max-content;
  max-width: 100%;
  margin: 0 auto;
  padding: 2px 0;

  @media (min-width: 640px) {
    gap: 8px;
  }
`;

export const MembersPaginationEllipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 32px;
  height: 36px;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  user-select: none;

  @media (min-width: 640px) {
    min-width: 40px;
    height: 40px;
  }
`;

export const MembersPaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: auto;
  min-width: 36px;
  height: 36px;
  padding: 0 6px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 8px;
  background: ${({ $active }) =>
    $active ? theme.primary : "hsl(var(--hp-background) / 0.4)"};
  color: ${({ $active }) =>
    $active ? theme.primaryForeground : theme.foreground};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: ${typography.weights.semibold};
  line-height: 1;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  .material-icons {
    font-size: 18px;
  }

  @media (min-width: 640px) {
    min-width: 40px;
    height: 40px;
    padding: 0 8px;
    font-size: 0.875rem;
  }

  &:hover:not(:disabled) {
    background: ${({ $active }) =>
      $active ? "hsl(var(--hp-primary) / 0.9)" : "hsl(var(--hp-background) / 0.6)"};
    border-color: ${({ $active }) =>
      $active ? theme.primary : "hsl(var(--hp-border) / 0.8)"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: 2px;
  }
`;

export const MembersCta = styled.section`
  position: relative;
  padding: 80px 16px;
  overflow: hidden;
  text-align: center;
`;

export const MembersCtaBg = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    hsl(32 95% 55% / 0.1) 0%,
    ${theme.bg} 50%,
    hsl(32 80% 45% / 0.05) 100%
  );
`;

export const MembersCtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 768px;
  margin: 0 auto;
`;

export const MembersCtaTitle = styled.h2`
  margin: 0 0 24px;
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 1.875rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.25;

  @media (min-width: 768px) {
    font-size: 3rem;
  }

  span {
    ${gradientTextStyles}
  }
`;

export const MembersCtaLead = styled.p`
  margin: 0 0 32px;
  color: ${theme.textMuted};
  font-size: 1.125rem;
  line-height: 1.625;
`;

export const MembersCtaButton = styled(PrimaryButton)`
  min-height: 56px;
  padding: 0 36px;
  font-size: 1rem;
`;

/* Post cards */
export const PostCardRoot = styled.article`
  position: relative;
  ${glassCardStyles}
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 16px;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: hsl(var(--hp-primary) / 0.4);
  }
`;

export const PostCardOverlayLink = styled(Link)`
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: 16px;
  ${cardLinkNoUnderline}

  span {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

export const FeaturedContributorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};
  font-size: 0.625rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.3;
  white-space: nowrap;
`;

export const FeaturedCardRoot = styled(PostCardRoot)`
  overflow: hidden;
  padding: 0;
  /* Lovable .glass-card has no extra drop shadow */
  box-shadow: none;
  transition: border-color 0.3s ease;
`;

export const FeaturedCardGrid = styled.div`
  display: grid;

  @media (min-width: 768px) {
    grid-template-columns: 1.1fr 1fr;
    min-height: 256px;
  }
`;

export const FeaturedCardMedia = styled.div`
  position: relative;
  min-height: 256px;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    hsl(32 95% 55% / 0.2) 0%,
    ${theme.bg} 50%,
    hsl(32 80% 45% / 0.1) 100%
  );

  @media (min-width: 768px) {
    height: 100%;
    min-height: 256px;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  ${FeaturedCardRoot}:hover & img {
    transform: scale(1.05);
  }
`;

export const FeaturedCardMediaGradient = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top right,
    hsl(var(--hp-background) / 0.7) 0%,
    hsl(var(--hp-background) / 0.1) 50%,
    transparent 100%
  );
`;

export const FeaturedBadge = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${theme.primary};
  color: ${theme.primaryForeground};
  font-size: 0.75rem;
  font-weight: ${typography.weights.bold};

  .material-icons {
    font-size: 14px;
  }
`;

export const PostTypePill = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: hsl(var(--hp-background) / 0.8);
  backdrop-filter: blur(8px);
  color: ${theme.foreground};
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};

  .material-icons {
    font-size: 14px;
    color: ${theme.primary};
  }
`;

export const FeaturedCardBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;

  @media (min-width: 768px) {
    padding: 32px;
  }

  & > a {
    margin-bottom: 16px;
  }

  & > p {
    margin-bottom: 20px;
  }

  & > div:nth-of-type(1) {
    margin-bottom: 20px;
  }
`;

export const GridCardInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px;
`;

export const GridCardMedia = styled.div`
  position: relative;
  height: 128px;
  margin: -8px -8px 16px;
  overflow: hidden;
  border-radius: 12px;
  background: hsl(var(--hp-secondary) / 0.4);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  ${PostCardRoot}:hover & img {
    transform: scale(1.05);
  }
`;

export const GridCardMediaGradient = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    hsl(var(--hp-background) / 0.6) 0%,
    transparent 100%
  );
`;

export const PostAuthorRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

export const PostAuthorLink = styled(Link)`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  ${cardLinkNoUnderline}

  &:hover {
    strong {
      color: ${theme.primary};
    }

    img {
      box-shadow: 0 0 0 2px hsl(var(--hp-primary) / 0.6);
    }
  }

  img {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 999px;
    object-fit: cover;
    box-shadow: 0 0 0 2px hsl(var(--hp-primary) / 0.3);
    transition: box-shadow 0.2s ease;
  }

  strong {
    display: block;
    overflow: hidden;
    color: ${theme.foreground};
    font-size: 0.875rem;
    font-weight: ${typography.weights.semibold};
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.2s ease;
  }

  span {
    display: block;
    overflow: hidden;
    color: ${theme.textMuted};
    font-size: 0.75rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const PostTypeBadge = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: hsl(var(--hp-primary) / 0.1);
  color: ${theme.primary};
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};

  .material-icons {
    font-size: 14px;
  }
`;

export const PostTitle = styled.h2`
  margin: 0 0 8px;
  color: ${theme.foreground};
  font-size: ${({ $featured }) => ($featured ? "1.875rem" : "1.125rem")};
  font-weight: ${typography.weights.bold};
  line-height: 1.3;
  transition: color 0.2s ease;

  ${PostCardRoot}:hover &,
  ${FeaturedCardRoot}:hover & {
    color: ${theme.primary};
  }

  @media (min-width: 768px) {
    font-size: ${({ $featured }) => ($featured ? "1.875rem" : "1.125rem")};
  }
`;

export const PostTitleFeatured = styled(PostTitle)`
  margin-bottom: 12px;
  font-size: 1.5rem;

  @media (min-width: 768px) {
    font-size: 1.875rem;
  }
`;

export const PostExcerpt = styled.p`
  margin: 0 0 16px;
  color: ${theme.textMuted};
  font-size: ${({ $compact }) => ($compact ? "0.875rem" : "1rem")};
  line-height: 1.625;

  ${({ $compact }) =>
    $compact &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

export const PostTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
`;

export const PostTag = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: hsl(var(--hp-secondary) / 0.6);
  color: ${theme.textMuted};
  font-size: ${({ $hash }) => ($hash ? "0.6875rem" : "0.8125rem")};

  ${({ $outline }) =>
    $outline &&
    `
    border: 1px solid hsl(var(--hp-border) / 0.6);
    background: transparent;
  `}
`;

export const PostMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  padding-top: ${({ $bordered }) => ($bordered ? "8px" : "0")};
  border-top: ${({ $bordered }) =>
    $bordered ? `1px solid hsl(var(--hp-border) / 0.4)` : "none"};
  color: ${theme.textMuted};
  font-size: 0.75rem;
`;

export const PostMetaDate = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .material-icons {
    font-size: 14px;
  }
`;

export const SkeletonFeatured = styled.div`
  height: 320px;
  border-radius: 16px;
  background: hsl(var(--hp-muted) / 0.5);
  animation: members-pulse 1.5s ease-in-out infinite;

  @keyframes members-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

export const SkeletonGrid = styled.div`
  display: grid;
  gap: 24px;
  margin-top: ${({ $noFeatured }) => ($noFeatured ? "0" : "24px")};

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > *:last-child:nth-child(odd) {
      grid-column: 1 / -1;
      width: 100%;
      max-width: calc((100% - 24px) / 2);
      justify-self: center;
    }
  }
`;

export const SkeletonCard = styled.div`
  height: 288px;
  border-radius: 16px;
  background: hsl(var(--hp-muted) / 0.5);
  animation: members-pulse 1.5s ease-in-out infinite;
`;
